import * as React from "react";

import { AccordionContainer, Accordion, Breadcrumbs, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import TutorialsService from "@app/tutorials/tutorialsService";
import { TutorialContent, loadingData } from "@app/tutorials/tutorialsShared"
import { Unauthorized } from "@app/shared/ErrorPages";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { JSX } from "react";
import { Roles, UserContext, UserInfo } from "@app/user";

interface TutorialData {
  newFeatures: TutorialContent[];
  qasGuides: TutorialContent[];
  webinars: TutorialContent[];
}

export default class QASTutorials extends React.Component<never, TutorialData> {
  constructor(props: never) {
    super(props);

    this.state = {
      newFeatures: loadingData,
      qasGuides: loadingData,
      webinars: loadingData
    }
  }

  componentDidMount() {
    this.loadContent();
  }

  private loadContent = () => {
    Promise.all(
      [
        this.supportService.showBlobSas("new-features"),
        this.supportService.showBlobSas("qas-user-guides"),
        this.supportService.showBlobSas("webinars")
      ])
      .then(([newFeaturesStr, qasGuidesStr, webinarsStr]) => {
        const newFeatures: TutorialContent[] = JSON.parse(newFeaturesStr);
        const qasGuides: TutorialContent[] = JSON.parse(qasGuidesStr);
        const webinars: TutorialContent[] = JSON.parse(webinarsStr);
        this.setState({ newFeatures, qasGuides, webinars });
      })
      .catch(e => {
        console.error(e);
        alert("Could not load content, please reload the page in a few moments.");
      })
  }

  private readonly supportService = new TutorialsService();

  public render() {
    const whatsNewList = this.rowPopulator(this.state.newFeatures);
    const webinarsList = this.rowPopulator(this.state.webinars);
    const guideList = this.headerRowPopulator(this.state.qasGuides);

    return (
      <UserContext.Consumer>
        {(user: UserInfo) => (
          user && user.roles.includes(Roles.SOFTWARE_LICENSE) ?
            (<Page documentTitle="Tutorials">
              <Breadcrumbs
                items={[
                  {
                    iconClass: "fa-question-circle",
                    text: "Help",
                  },
                  {
                    iconClass: "fa-pencil",
                    text: "Tutorials",
                  },
                ]}
              />
              <PageInner>
                <PageHeader title="Quest Analytics Tutorials" iconName={QesIcons.FontAwesome.Pencil} />
                <div className="row">
                  <div className="col-12">
                    <div className="ml-auto">
                      <a className="float-right text-decoration-none" href="mailto:support@questanalytics.com">
                        <i className="fa fa-envelope" /> support@questanalytics.com
                      </a>
                    </div>
                  </div>
                </div>

                {this.renderHeaderRow("New Features")}
                <PageSection>
                  <div className="row card-striped">
                    <div className="col-12 col-sm-8">
                      <AccordionContainer>
                        <Accordion title="New Features" testId="tutorials__new-features-accordion">
                          {whatsNewList}
                        </Accordion>
                      </AccordionContainer>
                    </div>
                  </div>
                </PageSection>

                {this.renderHeaderRow("Webinars")}
                <PageSection>
                  <div className="row card-striped">
                    <div className="col-12 col-sm-8">
                      <AccordionContainer>
                        <Accordion title="Webinars" testId="tutorials__webinars-accordion">
                          {webinarsList}
                        </Accordion>
                      </AccordionContainer>
                    </div>
                  </div>
                </PageSection>

                {guideList}
              </PageInner>
            </Page>)
            : (<Unauthorized />)
        )}
      </UserContext.Consumer>
    );
  }

  private renderHeaderRow(rowTitle: string) {
    return (
      <div className="row">
        <div className="col-12">
          <p className="lead mb0">{rowTitle} </p>
          <hr className="mt0" />
        </div >
      </div >
    );
  }

  private headerRowPopulator(rows: TutorialContent[]) {
    const buildRows = rows.map((row, index) => this.renderHeaderRowWithLink(row, index));

    return (
      <div className="tutorial-table">
        {buildRows}
      </div>
    );
  }

  private renderHeaderRowWithLink(row: TutorialContent, index: number) {

    return (
      <div className="row" key={index}>
        <div className="col-12">
          <p className="lead mb0">{row.title} - <a className="lead mb0" href={row.link} target="_blank" rel="noreferrer">{row.filename + '.' + row.filetype}</a></p>
          <hr className="mt0" />
        </div>
      </div>
    );
  }

  private rowPopulator(links: TutorialContent[]) {
    const buildRows = links.map((row, index) => this.renderLines(row, index));
    return (
      <div className="grid-striped">
        {buildRows}
      </div>
    );
  }

  private renderLines(row: TutorialContent, index: number) {
    let linkImageElement: JSX.Element = null;
    let titleStyle = "";
    switch (row.filetype) {
      case "pdf":
        linkImageElement = (
          <i className="fa fa-file-pdf-o text-danger" />
        );
        break;
      case "mp4":
        linkImageElement = (
          <i className="fa fa-play" />
        );
        titleStyle = "text-info font-italic";
        break;
      case "txt":
        linkImageElement = (
          <i className="fa fa-link text-info" />
        );
        titleStyle = "text-info font-italic";
        break;
      default:
        linkImageElement = (
          <i className="fa fa-play" />
        );
    }

    return (
      <div className={"d-flex flex-row align-items-center w-100 pb10 pt10 grid-row " + titleStyle} key={index}>

        <p className="ml10 mb0 mr10">
          <a className="text-decoration-none" style={{ color: "inherit" }} href={row.link} target="_blank" rel="noreferrer">
            {linkImageElement} <span className="mb0 mt0 ml10">{row.title}</span>
          </a>
        </p>
      </div>
    );
  }

}

export { QASTutorials };
